import { useEffect, useState } from 'react';

import BpkSelectableChip from '@skyscanner-internal/backpack-web/bpk-component-chip';
import BpkChevronDown from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/chevron-down';
import BpkChevronUp from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/chevron-up';
import {
  BpkBox,
  BpkBreakpoint,
  BpkFlex,
  BpkSpacing,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';

import CustomNavDesktopItem from './CustomNavDesktopItem';
import CustomNavMobileItem from './CustomNavMobileItem';

import type { CustomNavProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/CustomNavProps';

import STYLES from './CustomNav.module.scss';

export default function CustomNav({
  menuItems,
  mobileToggleMenuLabel,
  mobileVisitParentPageLabel,
}: CustomNavProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDesktopSubMenu, setOpenDesktopSubMenu] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add('custom-menu-open');
    } else {
      document.body.classList.remove('custom-menu-open');
    }
  }, [mobileMenuOpen]);

  return (
    <BpkBox
      as="nav"
      backgroundColor="surface-hero"
      paddingTop={BpkSpacing.MD}
      paddingBottom={BpkSpacing.Base}
    >
      <BpkVessel className={STYLES.Grid}>
        {/** Mobile Menu */}
        {/* Legacy max-width: 48rem (768px) => visible on base, hidden from Tablet (769px+) */}
        <BpkBox
          display={{ base: 'flex', [BpkBreakpoint.Tablet]: 'none' }}
          justifyContent="flex-end"
          width="100%"
        >
          <BpkSelectableChip
            accessibilityLabel="Press to open menu"
            type="on-dark"
            selected={mobileMenuOpen}
            trailingAccessoryView={
              mobileMenuOpen ? <BpkChevronUp /> : <BpkChevronDown />
            }
            onClick={() => {
              setMobileMenuOpen(!mobileMenuOpen);
            }}
          >
            {mobileToggleMenuLabel}
          </BpkSelectableChip>
        </BpkBox>

        <BpkBox
          data-testid="news-nav-mobile"
          backgroundColor="surface-hero"
          display={
            mobileMenuOpen
              ? { base: 'block', [BpkBreakpoint.Tablet]: 'none' }
              : 'none'
          }
          paddingTop={mobileMenuOpen ? BpkSpacing.Base : undefined}
          paddingBottom={mobileMenuOpen ? BpkSpacing.LG : undefined}
        >
          {menuItems.map((menuItem) => (
            <CustomNavMobileItem
              key={menuItem.id}
              menuItem={menuItem}
              mobileVisitParentPageLabel={mobileVisitParentPageLabel}
            />
          ))}
        </BpkBox>

        {/** Desktop Menu */}
        <BpkVessel
          data-testid="news-nav-desktop"
          className={STYLES.DesktopMenu}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              setOpenDesktopSubMenu(null);
            }
          }}
        >
          <BpkFlex position="relative" align="center" gap={BpkSpacing.XL}>
            {menuItems.map((menuItem) => (
              <CustomNavDesktopItem
                key={menuItem.id}
                menuItem={menuItem}
                onOpen={setOpenDesktopSubMenu}
                openMenuItemId={openDesktopSubMenu}
              />
            ))}
          </BpkFlex>
        </BpkVessel>
      </BpkVessel>
    </BpkBox>
  );
}
